import { type ReactNode, useEffect, useId, useRef } from 'react';

/**
 * Small UI pieces used by the notification screens.
 * TODO(integration): swap for the app's own Button / Modal / Switch if they exist.
 */

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
const buttonStyles: Record<ButtonVariant, string> = {
  primary: 'bg-emerald-700 text-white hover:bg-emerald-800 disabled:bg-gray-300 disabled:text-gray-600',
  secondary: 'border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 disabled:opacity-50',
  outline: 'border border-emerald-700 bg-white text-emerald-800 hover:bg-emerald-50 disabled:opacity-50',
  ghost: 'text-gray-700 hover:bg-gray-100 disabled:opacity-50',
  danger: 'bg-red-700 text-white hover:bg-red-800 disabled:bg-gray-300 disabled:text-gray-600',
};

export function Button({
  variant = 'secondary',
  className,
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return (
    <button
      type='button'
      className={cx(
        'inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 text-sm font-semibold transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed',
        buttonStyles[variant],
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type='button'
      role='switch'
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2',
        checked ? 'bg-emerald-700' : 'bg-gray-400',
        disabled && 'cursor-not-allowed opacity-70'
      )}
    >
      <span
        className={cx(
          'inline-block h-5 w-5 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0.5'
        )}
      />
    </button>
  );
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode }[];
  label: string;
}) {
  return (
    <div role='group' aria-label={label} className='inline-flex gap-1 rounded-lg bg-gray-100 p-1'>
      {options.map((o) => (
        <button
          key={o.value}
          type='button'
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cx(
            'min-h-9 flex-1 whitespace-nowrap rounded-md px-3 text-sm font-semibold transition-colors',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600',
            value === o.value ? 'bg-white text-emerald-800 shadow-sm' : 'text-gray-600 hover:text-gray-900'
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function SourceBadge({ source }: { source: 'default' | 'custom' | 'options' }) {
  const styles = {
    default: 'bg-gray-100 text-gray-700',
    custom: 'bg-blue-50 text-blue-800',
    options: 'bg-orange-50 text-orange-800',
  } as const;
  const text = { default: 'Default', custom: 'Custom', options: 'Options' } as const;
  return <span className={cx('rounded-full px-2.5 py-0.5 text-xs font-semibold', styles[source])}>{text[source]}</span>;
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'warning' | 'danger' | 'neutral'; children: ReactNode }) {
  const styles = {
    info: 'bg-emerald-50 text-emerald-900',
    warning: 'bg-orange-50 text-orange-900',
    danger: 'bg-red-50 text-red-900',
    neutral: 'bg-gray-100 text-gray-800',
  } as const;
  return <div className={cx('rounded-md px-3 py-2.5 text-sm leading-relaxed', styles[tone])}>{children}</div>;
}

/** Accessible modal: focus moves in, Escape closes, focus returns on close, Tab stays inside. */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'lg',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg' | 'xl';
}) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const focusables = () =>
      panel
        ? Array.from(
            panel.querySelectorAll<HTMLElement>(
              'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
          )
        : [];
    (focusables()[0] ?? panel)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      } else if (e.key === 'Tab') {
        const items = focusables();
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previouslyFocused?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  const width = { md: 'max-w-lg', lg: 'max-w-3xl', xl: 'max-w-4xl' }[size];
  return (
    <div className='fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-gray-900/50 p-4 sm:p-10'>
      <div
        ref={panelRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cx('flex max-h-full w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl', width)}
      >
        <div className='flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5'>
          <div className='flex flex-col gap-1'>
            <h2 id={titleId} className='text-xl font-semibold text-gray-900'>
              {title}
            </h2>
            {description && <div className='text-sm text-gray-600'>{description}</div>}
          </div>
          <button
            type='button'
            onClick={onClose}
            aria-label='Close'
            className='flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600'
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.8'
              strokeLinecap='round'
              aria-hidden='true'
            >
              <path d='M6 6l12 12M18 6L6 18' />
            </svg>
          </button>
        </div>
        <div className='min-h-0 flex-1 overflow-y-auto'>{children}</div>
        {footer && <div className='border-t border-gray-200 px-6 py-4'>{footer}</div>}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  tone = 'primary',
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size='md'
      footer={
        <div className='flex justify-end gap-3'>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant={tone} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <div className='px-6 py-5 text-sm leading-relaxed text-gray-700'>{message}</div>
    </Modal>
  );
}
